import { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Trash2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useMembers, MemberAvatar } from "@/lib/members";
import { useAuth } from "@/lib/auth";
import { extractErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

function relTime(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Highlight @mention tokens in a comment body.
function renderBody(body) {
  const parts = String(body).split(/(@[\w][\w'’-]*)/g);
  return parts.map((p, i) =>
    p.startsWith("@")
      ? <span key={i} className="font-medium text-primary">{p}</span>
      : <span key={i}>{p}</span>
  );
}

export function RecordComments({ recordId }) {
  const { members, usersById } = useMembers();
  const { user } = useAuth();
  const meId = user?.id;

  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]); // user_ids inserted via picker
  const [posting, setPosting] = useState(false);
  const [picker, setPicker] = useState(null); // {query} when typing @…
  const taRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/records/${recordId}/comments`);
      setComments(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [recordId]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const upto = v.slice(0, e.target.selectionStart);
    const m = upto.match(/@(\w*)$/);
    setPicker(m ? { query: m[1].toLowerCase() } : null);
  };

  const suggestions = useMemo(() => {
    if (!picker) return [];
    return members
      .filter((m) => (m.name || m.email || "").toLowerCase().includes(picker.query))
      .slice(0, 6);
  }, [picker, members]);

  const pick = (m) => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@(\w*)$/, `@${m.name || m.email} `);
    const after = text.slice(caret);
    setText(before + after);
    setMentions((ms) => (ms.includes(m.user_id) ? ms : [...ms, m.user_id]));
    setPicker(null);
    setTimeout(() => ta && ta.focus(), 0);
  };

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      // keep only mentions whose names still appear in the text
      const used = mentions.filter((uid) => {
        const nm = usersById[uid]?.name || usersById[uid]?.email || "";
        return nm && text.includes(`@${nm}`);
      });
      await api.post(`/records/${recordId}/comments`, { body: text.trim(), mentions: used });
      setText(""); setMentions([]); setPicker(null);
      load();
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/comments/${id}`); load(); }
    catch (e) { toast.error(extractErrorMessage(e)); }
  };

  return (
    <div data-testid="record-comments">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Comments</h3>
        {comments.length > 0 && <span className="text-xs text-muted-foreground">{comments.length}</span>}
      </div>

      <div className="space-y-3 mb-4">
        {comments.length === 0 && <p className="text-sm text-muted-foreground">No comments yet. Start the conversation.</p>}
        {comments.map((c) => {
          const u = usersById[c.author_id];
          return (
            <div key={c.id} className="flex gap-2.5 group">
              <MemberAvatar user={u} size={28} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{u?.name || u?.email || "Member"}</span>
                  <span className="text-[11px] text-muted-foreground">{relTime(c.created_at)}</span>
                  {c.author_id === meId && (
                    <button onClick={() => del(c.id)} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete" data-testid={`del-comment-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{renderBody(c.body)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="relative">
        <textarea
          ref={taRef}
          value={text}
          onChange={onChange}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          placeholder="Write a comment…  Use @ to mention a teammate."
          rows={2}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
          data-testid="comment-input"
        />
        {picker && suggestions.length > 0 && (
          <div className="absolute z-20 mt-1 w-64 rounded-md border border-border bg-white shadow-md overflow-hidden">
            {suggestions.map((m) => (
              <button key={m.user_id} onClick={() => pick(m)}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 hover:bg-muted text-left"
                data-testid={`mention-${m.user_id}`}>
                <MemberAvatar user={m} size={20} />
                <span className="text-sm truncate">{m.name || m.email}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-muted-foreground">⌘/Ctrl + Enter to post</span>
          <Button size="sm" onClick={submit} disabled={posting || !text.trim()} data-testid="post-comment">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {posting ? "Posting…" : "Comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
